<template>
	<!-- Keep Sessions top placement while allowing the field to scroll on short viewports. -->
	<v-dialog
		v-model="open"
		:width="470"
		max-width="calc(100% - 32px)"
		max-height="calc(100dvh - 96px)"
		content-class="media-caption-dialog"
		scrollable
		@after-leave="emit('afterLeave')">
		<v-form v-model="validity" ref="form">
			<v-card class="pa-2" elevation="0">
				<v-card-text>
					<AppTextarea
						v-model="caption"
						variant="outlined"
						hide-details="auto"
						color="primary"
						max-height="300"
						rows="3"
						:disabled="saving"
						:character-limit="200"
						over-limit-message="Please enter a shorter caption."
						placeholder="Say a little something about what's happening here..."
						@keydown="stopArrowNavigation">
						<template #label><FieldLabel label="Caption (Optional)" /></template>
					</AppTextarea>
					<p v-if="error" role="alert">{{ error }}</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="grey-darken-3" variant="text" @click="emit('cancel')">Cancel</v-btn>
					<v-btn
						color="primary"
						variant="flat"
						class="media-caption-save"
						:disabled="saving"
						:loading="saving"
						@click="emit('save')"
						>Save</v-btn
					>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-form>
	</v-dialog>
</template>

<script setup lang="ts">
	import { useTemplateRef } from "vue";
	import type { VForm } from "vuetify/components";
	import AppTextarea from "../components/AppTextarea.vue";
	import FieldLabel from "../components/FieldLabel.vue";

	defineProps<{ saving?: boolean; error?: string }>();
	const open = defineModel<boolean>({ default: false });
	const caption = defineModel<string>("caption", { default: "" });
	const validity = defineModel<boolean | null>("validity", { default: null });
	const emit = defineEmits<{ cancel: []; save: []; afterLeave: [] }>();
	const form = useTemplateRef<InstanceType<typeof VForm>>("form");
	function stopArrowNavigation(event: KeyboardEvent) {
		if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"].includes(event.code)) event.stopPropagation();
	}
	defineExpose({
		validate: async () => {
			if (!form.value) throw new Error("Caption form is not mounted");
			return form.value.validate();
		},
		resetValidation: () => form.value?.resetValidation()
	});
</script>

<style scoped>
	:global(.media-caption-dialog) {
		align-self: flex-start;
		margin-top: 4.5rem !important;
	}
	/* Admin's dialog presentation, explicit so the host app's Vuetify settings cannot change it. The card radius is
	   !important, like the rounded-lg utility it replaces, because Vuetify's `.v-dialog > … > form > .v-card` outranks it. */
	.v-card {
		border-radius: 0.5rem !important;
		font-family: Inter;
	}
	/* Admin's global .v-card-text padding; !important outranks Vuetify's `.v-dialog > … > form > .v-card > .v-card-text`. */
	.v-card-text {
		padding: 0.5rem !important;
	}
	:deep(.v-field) {
		border-radius: 0.5rem;
	}
	:deep(.v-label) {
		font-size: 0.875rem;
	}
	.v-btn {
		border-radius: 1.5rem;
		font-family: Inter;
		font-size: 0.875rem;
		font-weight: 600;
		letter-spacing: normal;
		text-transform: none;
	}
	/* The label sits in .v-btn__content, which async's base.css `*` reset sets to normal weight. */
	.v-btn :deep(.v-btn__content) {
		font-weight: inherit;
	}
	.v-btn.media-caption-save {
		border-radius: 0.25rem;
	}
</style>
